import { useLocation, useParams } from 'react-router';

import { useAbsoluteRoutes } from '../../../routes';
import { useCurrentUser } from '../current-user';
import { useCurrentUserRolesForWebcast } from '../use-current-user-roles-for-webcast';

export function useUserPermissions(channelId?: string) {
  const { webcastId = '' } = useParams();
  const { isOnlyModeratorForEvent } = useCurrentUserRolesForWebcast(webcastId);
  const { isAdminUser, isEditorUser, isProducerUser, isModeratorUser } = useCurrentUser(channelId);
  const { pathname } = useLocation();
  const routes = useAbsoluteRoutes();

  // If user is only moderator for this event, override channel-level roles (except admin)
  const effectiveIsEditorUser = isOnlyModeratorForEvent ? false : isEditorUser;
  const effectiveIsProducerUser = isOnlyModeratorForEvent ? false : isProducerUser;
  const effectiveIsModeratorUser = isOnlyModeratorForEvent ? true : isModeratorUser;

  const isEventDeletionAllowed = isAdminUser || effectiveIsEditorUser;
  const isEventDuplicationAllowed = isAdminUser || effectiveIsEditorUser;
  const isEventEditingAllowed = isAdminUser || effectiveIsEditorUser;
  const isEventCreationAllowed = isAdminUser || effectiveIsEditorUser;
  const isEventReportingAllowed = isAdminUser || effectiveIsEditorUser;
  const isEventArchivingAllowed = isAdminUser || effectiveIsEditorUser;

  const isAccessToUserPageAllowed = isAdminUser;
  const isAccessToChannelSettingsAllowed = isAdminUser;
  const isAccessToBrandingPageAllowed = isAdminUser;
  const isAccessToArchivedEventsAllowed = isAdminUser || effectiveIsEditorUser;

  const isAccessToEventSetupAllowed = isAdminUser || effectiveIsEditorUser || effectiveIsProducerUser;
  const isEditorAndModerator = effectiveIsEditorUser && effectiveIsModeratorUser;

  const isAccessToOperatorAllowed = isAdminUser || effectiveIsProducerUser || effectiveIsModeratorUser;
  const isAccessToOperatorMessagingAllowed = isAdminUser || effectiveIsModeratorUser;
  const isFullAccessToOperatorAllowed = isAdminUser || effectiveIsProducerUser;
  const isModeratorAccessingMessagingOperator =
    effectiveIsModeratorUser && pathname.includes(routes.operator_messaging);

  const hasNoPermissions =
    !isAdminUser && !effectiveIsEditorUser && !effectiveIsProducerUser && !effectiveIsModeratorUser;

  return {
    hasNoPermissions,
    isAccessToArchivedEventsAllowed,
    isAccessToBrandingPageAllowed,
    isAccessToChannelSettingsAllowed,
    isAccessToEventSetupAllowed,
    isAccessToOperatorAllowed,
    isAccessToOperatorMessagingAllowed,
    isAccessToUserPageAllowed,
    isAdminUser,
    isEditorAndModerator,
    isEventCreationAllowed,
    isEventDeletionAllowed,
    isEventDuplicationAllowed,
    isEventEditingAllowed,
    isEventReportingAllowed,
    isEventArchivingAllowed,
    isFullAccessToOperatorAllowed,
    isModeratorAccessingMessagingOperator,
  };
}
